import { QueryKey, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  BidQuery,
  BidSupplierBuyerNoticeSummaryQuery,
  SupplierBuyerNoticeSummaryRequest,
} from "lib/generated/app-service-gql/graphql";
import { useGraphQlQuery } from "../../useGraphQlClient";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "../../utils";

const BID_QUERY = graphql(`
  query bid($id: String!) {
    bid(id: $id) {
      id
      name
      description
      value
      assignedTo {
        guid
        firstName
        lastName
        active
      }
      clarificationDeadline
      submissionDate
      startDate
      awardDate
      createdAt
      updatedAt
      notice {
        guid
        url
        name
        stage
        stagev2
        expiryDate
        closeDate
        publishDate
        procurementStage {
          id
        }
        lots {
          suppliers {
            guid
            name
          }
        }
        buyers {
          guid
          name
          logoUrl
          createdAt
        }
      }
      stage {
        id
        name
        color
        iconColor
        iconName
        transitionGroup
      }
      buyers {
        id
        name
        logoUrl
      }
      assignedTo {
        guid
        firstName
        lastName
        active
      }
      suppliers {
        id
        name
        logoUrl
      }
      customBuyers {
        id
        name
      }
      framework {
        id
        title
        stage
        tenderPeriod {
          endDate
        }
        contractPeriod {
          endDate
        }
        buyers {
          id
          name
        }
      }
      bidResponses {
        id
        stageId
      }
      lead {
        id
        name
      }
      bidReport {
        id
        createdAt
        createdBy {
          guid
          firstName
          lastName
          active
        }
      }
      qualificationReport {
        id
        createdAt
        createdBy {
          guid
          firstName
          lastName
          active
        }
      }
      comments {
        id
        content
        createdAt
        updatedAt
        author {
          guid
          firstName
          lastName
          active
        }
      }
      buyerActivity {
        status
        questions {
          question
          answer
          sources {
            type
            id
            url
            title
            chunkNumber
            documentType
            filePath
            isDeleted
            citationId
            locations {
              pageNumber
              bbox {
                x
                y
                width
                height
              }
            }
          }
        }
        relatedNotices {
          guid
          name
          stagev2
          expiryDate
          closeDate
          publishDate
          value
          procurementStage {
            id
            stage
          }
          lots {
            suppliers {
              guid
              name
            }
          }
        }
      }
      winThemes {
        id
        title
        description
        sources {
          title
          id
          type
          url
          chunkNumber
          documentType
          filePath
          locations {
            pageNumber
            bbox {
              x
              y
              width
              height
            }
          }
          isDeleted
          citationId
        }
      }
    }
  }
`);

export type BidDetails = BidQuery["bid"];

export function useBid(
  { id }: { id: string },
  options?: UseQueryOptions<BidQuery, unknown, BidQuery, QueryKey>,
) {
  const { data, ...rest } = useGraphQlQuery(["bid", id], BID_QUERY, [{ id }], {
    ...options,
    ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
  });
  return { data: data?.bid, ...rest };
}

const BID_SUPPLIER_BUYER_NOTICE_SUMMARY_FRAGMENT_QUERY = graphql(`
  query bidSupplierBuyerNoticeSummary(
    $id: String!
    $supplierBuyerNoticeSummaryRequest: SupplierBuyerNoticeSummaryRequest!
  ) {
    bid(id: $id) {
      supplierBuyerNoticeSummary(
        SupplierBuyerNoticeSummaryRequest: $supplierBuyerNoticeSummaryRequest
      ) {
        supplier {
          name
          id
          logoUrl
        }
        allAwards {
          totalCount
          totalValue
        }
        similarAwards {
          totalCount
          totalValue
        }
      }
    }
  }
`);

export function useBidSupplierBuyerNoticeSummary(
  bidId: string,
  searchRequest: SupplierBuyerNoticeSummaryRequest,
  options?: UseQueryOptions<
    BidSupplierBuyerNoticeSummaryQuery,
    unknown,
    BidSupplierBuyerNoticeSummaryQuery,
    QueryKey
  >,
) {
  const { data, ...rest } = useGraphQlQuery(
    ["bid", bidId, "supplierBuyerNoticeSummary", searchRequest],
    BID_SUPPLIER_BUYER_NOTICE_SUMMARY_FRAGMENT_QUERY,
    [{ id: bidId, supplierBuyerNoticeSummaryRequest: searchRequest }],
    {
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      ...options,
    },
  );

  return {
    data: data?.bid.supplierBuyerNoticeSummary,
    ...rest,
  };
}

export type UseBidSupplierBuyerNoticeSummary = typeof useBidSupplierBuyerNoticeSummary;
